import React, { ReactElement } from 'react';
import { cn } from 'utils';

export type ShimmerProps = {
  className?: string;
};

/**
 * Pulsing placeholder block shown while dashboard data is loading. Matches the
 * app-wide skeleton convention (animate-pulse + neutral gray fill) used by
 * components such as `InfoRow` and `ReportDashboardStatsOverview`.
 */
export function Shimmer({ className }: ShimmerProps): ReactElement {
  return (
    <div
      aria-hidden
      className={cn(
        'odin-animate-pulse odin-bg-gray-200 odin-rounded-md',
        className,
      )}
    />
  );
}

export type JobsiteOverviewEmptyStateProps = {
  // Matches the shape of the app's FontAwesome icon components (see `getFaIcon`),
  // which only accept `className`. The icon is decorative — its accessibility is
  // handled by the `aria-hidden` wrapper below, not by props on the icon itself.
  icon: (props: { className?: string }) => ReactElement;
  message: string;
  className?: string;
};

/**
 * Compact icon + message shown inside a dashboard card when a fetch has
 * completed but returned no data for the selected date. Used to fill the
 * larger visual regions (e.g. an empty chart) that would otherwise render
 * as a bare, broken-looking blank.
 */
export function JobsiteOverviewEmptyState({
  icon: Icon,
  message,
  className,
}: JobsiteOverviewEmptyStateProps): ReactElement {
  return (
    <div
      className={cn(
        'odin-flex odin-flex-col odin-items-center odin-justify-center',
        'odin-gap-2 odin-text-center',
        className,
      )}
    >
      <span aria-hidden="true" className="odin-inline-flex">
        <Icon className="odin-text-4xl odin-text-gray-300" />
      </span>
      <span className="odin-text-sm odin-text-odin-dark-gray">{message}</span>
    </div>
  );
}
